import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { CheckCircle2, DatabaseBackup, FlaskConical, Loader2, Play, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/backups`;

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('fr-FR') : '—';
const fmtSize = (b) => !b ? '—' : b > 1e9 ? `${(b / 1e9).toFixed(2)} Go` : b > 1e6 ? `${(b / 1e6).toFixed(1)} Mo` : `${Math.round(b / 1e3)} Ko`;

const StatusBadge = ({ status }) => status === 'ok'
  ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1"><CheckCircle2 className="h-3 w-3" />OK</Badge>
  : <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 gap-1"><XCircle className="h-3 w-3" />Échec</Badge>;

export default function AdminBackups() {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [retention, setRetention] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([axios.get(`${API}/status`), axios.get(`${API}/runs`)]);
      setStatus(s.data);
      setRuns(r.data);
      setRetention(String(s.data.settings.retention_count));
    } catch (e) { toast.error('Erreur de chargement'); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runNow = async () => {
    setBusy(true);
    toast.info('Sauvegarde en cours...');
    try {
      const { data } = await axios.post(`${API}/run`, {}, { timeout: 300000 });
      data.status === 'ok'
        ? toast.success(`✓ Sauvegarde réussie et vérifiée (${fmtSize(data.size_bytes)})`)
        : toast.error(`Échec : ${data.error}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  const testRestore = async (runId) => {
    setBusy(true);
    toast.info('Restauration de test en cours (base de test isolée)...');
    try {
      const { data } = await axios.post(`${API}/${runId}/test-restore`, {}, { timeout: 300000 });
      data.ok
        ? toast.success(`✓ Restauration vérifiée : ${data.documents_restored} documents dans ${data.collections_restored} collections`)
        : toast.error(`Échec restauration : ${data.error || data.mismatches?.join(', ')}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  const saveRetention = async () => {
    try {
      await axios.put(`${API}/settings`, { retention_count: parseInt(retention, 10) });
      toast.success('Rétention mise à jour');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Valeur invalide (3-90)'); }
  };

  const last = status?.last_success;
  return (
    <AdminLayout title="Sauvegardes" subtitle="Continuité d'activité — sauvegarde quotidienne automatique à 2 h 30, vérification intégrée">
      <div className="space-y-4" data-testid="admin-backups">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="border-border"><CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Dernière sauvegarde réussie</p>
            <p className="font-semibold" data-testid="backup-last-success">{fmtDT(last?.finished_at)}</p>
            {status?.last_run && status.last_run.status !== 'ok' && (
              <p className="text-xs text-red-600 mt-1">⚠ Dernière tentative en échec</p>)}
          </CardContent></Card>
          <Card className="border-border"><CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Taille</p>
            <p className="font-semibold" data-testid="backup-size">{fmtSize(last?.size_bytes)}</p>
            <p className="text-xs text-muted-foreground">{last?.documents ?? '—'} documents · {last?.collections ?? '—'} collections</p>
          </CardContent></Card>
          <Card className="border-border"><CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Fichiers inclus</p>
            <p className="font-semibold" data-testid="backup-files">{last?.files_count ?? '—'}</p>
            <p className="text-xs text-muted-foreground">{status?.local_archives ?? 0} archives conservées</p>
          </CardContent></Card>
          <Card className="border-border"><CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Destination</p>
            <p className="font-semibold" data-testid="backup-destination">{status?.s3_configured ? 'S3 externe' : 'Disque local'}</p>
            {!status?.s3_configured && <p className="text-xs text-amber-600 mt-1">S3 à configurer en production</p>}
          </CardContent></Card>
        </div>

        <Card className="border-border">
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2"><DatabaseBackup className="h-4 w-4" />Actions</CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Conserver</span>
              <Input type="number" min={3} max={90} value={retention} onChange={(e) => setRetention(e.target.value)}
                onBlur={saveRetention} className="w-20" data-testid="backup-retention-input" />
              <span className="text-xs text-muted-foreground">archives</span>
            </div>
          </CardHeader>
          <CardContent className="flex gap-2 flex-wrap">
            <Button onClick={runNow} disabled={busy} className="gap-2" data-testid="backup-run-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}Lancer une sauvegarde maintenant
            </Button>
            {last && (
              <Button variant="outline" onClick={() => testRestore(last.id)} disabled={busy} className="gap-2" data-testid="backup-test-restore-btn">
                <FlaskConical className="h-4 w-4" />Tester une restauration
              </Button>
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader><CardTitle className="text-base">Historique</CardTitle></CardHeader>
          <CardContent>
            {runs.length === 0 ? <p className="text-sm text-muted-foreground">Aucune sauvegarde pour le moment.</p> : (
              <ul className="divide-y divide-border">
                {runs.map((r) => (
                  <li key={r.id + r.started_at} className="py-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`backup-run-${r.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{fmtDT(r.started_at)} · {r.trigger === 'manual' ? 'Manuelle' : 'Planifiée'}</p>
                      <p className="text-xs text-muted-foreground">
                        {fmtSize(r.size_bytes)} · {r.documents ?? '?'} docs · {r.files_count ?? '?'} fichiers
                        {r.error && <span className="text-red-600"> · {r.error}</span>}
                        {(r.restore_tests || []).length > 0 && ` · ${r.restore_tests.length} test(s) de restauration`}
                      </p>
                    </div>
                    <StatusBadge status={r.status} />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
